import { useState, type ReactNode } from 'react'

import type {
  EligibilityState,
  Mobility,
  TransportOptions,
  TransportService,
} from '../../api/transport'
import { amtcDialNumber, amtcDisplayNumber } from '../../lib/amtc'
import { useBooking } from './booking'
import { Gate } from './Gate'
import { ProgressBar } from './ProgressBar'
import { SERVICE_NAME, serviceWhy } from './services'

/** States a passenger can book online, now or with a caregiver (Screen 4). */
const ONLINE: ReadonlyArray<EligibilityState> = [
  'recommended',
  'allowed',
  'needs_caregiver',
]

/**
 * The booking flow's Screen 1 (M39-T09, spec §C.3.1): the safety check, the
 * mobility and oxygen questions (owner Q5), and the recommendation, read from
 * the server's matrix so the page and the checkout cannot disagree.
 *
 * The tick is asked again whenever this screen is shown: it is a safety-check
 * answer and is never kept. Mobility and oxygen are, so Back keeps them.
 */
export function StartScreen({
  options,
  focusHeading,
  onStop,
  onContinue,
  keepService = false,
  onBack,
}: {
  options: TransportOptions
  focusHeading: boolean
  onStop: () => void
  /** With the answers, so the flow can re-check a kept service. */
  onContinue: (answers: { mobility: Mobility; usesOxygen: boolean }) => void
  /** Editing from Screen 4 (M39-T12): the flow re-checks the service
   * itself, so a changed answer does not clear it here. */
  keepService?: boolean
  onBack?: () => void
}): ReactNode {
  const { booking, update } = useBooking()
  const [planned, setPlanned] = useState(false)
  const [mobility, setMobility] = useState<Mobility | null>(booking.mobility)
  const [oxygen, setOxygen] = useState<boolean | null>(booking.usesOxygen)

  const row = (m: Mobility, o: boolean) =>
    options.eligibility.find((r) => r.mobility === m && r.uses_oxygen === o)

  // Stretcher capacity 0 makes Patient Transport phone-only whatever the
  // oxygen answer, so the desk message need not wait for it.
  const stretcherByPhone =
    mobility === 'stretcher' &&
    row('stretcher', oxygen ?? false)?.services.pts?.state === 'phone_only'
  const cells =
    mobility !== null && oxygen !== null
      ? Object.entries(row(mobility, oxygen)?.services ?? {})
      : []
  const best = cells.find(([, c]) => c.state === 'recommended')?.[0] as
    | TransportService
    | undefined
  // Needs both answers (no row until then). A stretcher with no stretcher
  // vehicle has nothing bookable online, so it cannot go on either.
  const bookable = cells.some(([, c]) => ONLINE.includes(c.state))
  const canContinue = planned && bookable

  const desk = amtcDialNumber()
  const deskShown = amtcDisplayNumber() ?? desk

  let notice: ReactNode = null
  if (planned && stretcherByPhone) {
    notice = (
      <>
        We can&apos;t book stretcher transport online yet.{' '}
        {desk ? (
          <>
            Call the transport desk on{' '}
            <a
              href={`tel:${desk}`}
              className="font-semibold text-accent underline underline-offset-2"
            >
              {deskShown}
            </a>
            .
          </>
        ) : (
          'Please try again later.'
        )}
      </>
    )
  } else if (planned && mobility !== null && best) {
    notice = (
      <>
        We&apos;ll recommend{' '}
        <span className="font-semibold text-ink-1">{SERVICE_NAME[best]}</span> —{' '}
        {serviceWhy(best, mobility)}.
        {oxygen && ' The passenger brings their own portable oxygen.'}
      </>
    )
  }

  return (
    <Gate
      focusHeading={focusHeading}
      {...(onBack ? { onBack } : {})}
      subtitle="Scheduled, pre-paid rides to our network hospitals."
      progress={<ProgressBar current={0} />}
      planned={planned}
      onPlanned={setPlanned}
      mobility={mobility}
      onMobility={setMobility}
      extra={
        <fieldset
          disabled={!planned}
          aria-describedby="oxygen-help"
          className="disabled:opacity-50"
        >
          <legend className="font-mono text-[10px] uppercase tracking-wide text-ink-3">
            Does the passenger use oxygen?
          </legend>
          <p id="oxygen-help" className="mt-0.5 text-[12px] text-ink-3">
            {planned
              ? 'This also decides which vehicle we can send.'
              : 'Tick “No” above first. This also decides which vehicle we can send.'}
          </p>
          <div className="mt-2 flex gap-2">
            {([true, false] as const).map((value) => (
              <label
                key={String(value)}
                className={`flex flex-1 items-center gap-2.5 rounded-r3 border px-3 py-2.5 ${
                  oxygen === value
                    ? 'border-accent bg-accent-bg'
                    : 'border-line-2 bg-bg-app'
                }`}
              >
                <input
                  type="radio"
                  name="oxygen"
                  checked={oxygen === value}
                  onChange={() => setOxygen(value)}
                  className="h-4 w-4 shrink-0 accent-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                />
                <span className="text-[13px] font-medium text-ink-1">
                  {value ? 'Yes' : 'No'}
                </span>
              </label>
            ))}
          </div>
        </fieldset>
      }
      notice={notice}
      canContinue={canContinue}
      onStop={onStop}
      onContinue={() => {
        const changed =
          mobility !== booking.mobility || oxygen !== booking.usesOxygen
        update({
          gatePassedAt: new Date().toISOString(),
          mobility,
          usesOxygen: oxygen,
          // A new answer can rule out the service chosen on Screen 2.
          ...(changed && !keepService ? { service: null } : {}),
        })
        if (mobility !== null && oxygen !== null) {
          onContinue({ mobility, usesOxygen: oxygen })
        }
      }}
    />
  )
}
